/**
 * The two lanes an inline ask rides (the SSE lane or the Line), the pure part:
 * the Line's generator made safe to read, and the once-per-ask count. Moved out
 * of useComposerConverse to keep it under its 500-line ratchet.
 */
import type {
  AnalyticsDelegationSurface,
  ConverseRequest,
  RouterEvent,
} from "@bash/shared";
import { captureComposerSubmitted } from "@/lib/analytics-v1";

/** The Line's typed door, as the hook calls it: the words, the send's own
 *  signal (a dismiss or a newer send aborts it), and the send's request id and
 *  the id of the send it cancelled, so two quick typed sends on a call are one
 *  ask. Null when the line is not open. */
export type LineDoor = (
  text: string,
  signal?: AbortSignal,
  keys?: Pick<ConverseRequest, "idempotencyKey" | "replaces">
) => AsyncGenerator<RouterEvent> | null;

/** The line's door throws instead of yielding when there is no token
 *  (not_signed_in), so a line generator can reject on its first pull. Here
 *  that rejection becomes the stream's own error event and rides the same
 *  reduction as any other: the card lands on its error state instead of
 *  sticking on "thinking" behind an unhandled rejection. */
export async function* settled(
  source: AsyncGenerator<RouterEvent>
): AsyncGenerator<RouterEvent> {
  try {
    yield* source;
  } catch (err) {
    yield {
      type: "error",
      code: "internal",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Analytics v1, once per ask: the line owns its send, so it counts now; the
 *  SSE lane at its first event, through the count this returns (a failure
 *  before that hands the ask to the classic rail, which counts it). */
export function countSubmitted(
  surface: AnalyticsDelegationSurface,
  text: string,
  taskId: string | undefined,
  viaLine: AsyncGenerator<RouterEvent> | null
): () => void {
  const submitted = () => captureComposerSubmitted(surface, text, taskId);
  if (viaLine) submitted();
  return submitted;
}
